import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { SectionHeader } from "@/components/SectionHeader";
import { CTASection } from "@/components/CTASection";
import { VALUES, IMAGES } from "@/data/content";

export default function About() {
  return (
    <>
      <section className="container-site pb-16 pt-16 sm:pt-24" data-testid="about-hero">
        <p className="overline-label">About us</p>
        <h1 className="mt-5 max-w-3xl font-heading text-4xl font-semibold leading-[1.08] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
          <MaskedLine delay={0.1}>Growth happens through</MaskedLine>
          <MaskedLine delay={0.25}>
            <span className="text-terracotta-500">understanding people.</span>
          </MaskedLine>
        </h1>
        <Reveal delay={0.4}>
          <p className="mt-7 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
            Evol &amp; Troppus was founded by a certified Behavior Technician (BT/RBT)
            who spent years working directly with children and families — in living
            rooms, classrooms, and everywhere behavior actually happens. Again and
            again, the same truth showed up: progress sticks when caregivers feel
            confident. That is why we exist — to empower caregivers through education,
            practical strategies, and genuine collaboration with the providers already
            on your child's team.
          </p>
        </Reveal>
      </section>

      <section className="container-site pb-24" data-testid="mission-vision-section">
        <div className="grid gap-6 md:grid-cols-2">
          <Reveal>
            <div className="card-lift h-full rounded-3xl bg-forest p-10 text-cream shadow-soft dark:bg-forest-800">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-cream/10">
                <Target className="h-6 w-6 text-terracotta-400" />
              </span>
              <h2 className="mt-7 font-heading text-2xl font-semibold">Our Mission</h2>
              <p className="mt-4 text-sm leading-relaxed text-cream/80 md:text-base">
                To make evidence-informed family support feel human — equipping caregivers
                with the understanding, skills, and confidence to create meaningful,
                lasting change at home.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="card-lift h-full rounded-3xl border border-border bg-card p-10 shadow-soft">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-terracotta-500/12">
                <Eye className="h-6 w-6 text-terracotta-500" />
              </span>
              <h2 className="mt-7 font-heading text-2xl font-semibold text-foreground">Our Vision</h2>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground md:text-base">
                A world where every caregiver has access to compassionate,
                neurodiversity-affirming coaching and education — and feels confident,
                connected, and supported in everyday life.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-border bg-card py-24 sm:py-32" data-testid="values-section">
        <div className="container-site">
          <Reveal>
            <SectionHeader
              overline="Our values"
              title="The Evol & Troppus manifesto"
              description="Five commitments. Every plan, every session, every conversation."
            />
          </Reveal>
          <div className="mt-16 space-y-6">
            {VALUES.map((v, i) => (
              <Reveal key={v.num} delay={i * 0.05}>
                <div
                  data-testid={`value-chapter-${v.num}`}
                  className={`group grid items-center gap-6 rounded-3xl border border-border bg-background p-8 shadow-soft transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lift sm:p-10 md:grid-cols-12 dark:hover:shadow-soft-dark ${
                    i % 2 === 1 ? "md:[&>div:first-child]:order-2" : ""
                  }`}
                >
                  <div className="md:col-span-3">
                    <span className="font-heading text-6xl font-semibold text-sage-200 transition-colors duration-300 group-hover:text-terracotta-500/40 sm:text-7xl dark:text-forest-800">
                      {v.num}
                    </span>
                  </div>
                  <div className="md:col-span-9">
                    <h3 className="font-heading text-xl font-semibold text-foreground sm:text-2xl">{v.title}</h3>
                    <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground md:text-base">
                      {v.text}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="container-site py-24 sm:py-32" data-testid="about-story-section">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -inset-4 -z-10 rounded-3xl bg-sage-200/50 dark:bg-forest-800/40" />
              <img
                src={IMAGES.architecture}
                alt="Warm minimalist architecture with soft light"
                className="h-[380px] w-full rounded-3xl object-cover shadow-lift dark:shadow-soft-dark"
                loading="lazy"
              />
            </div>
          </Reveal>
          <div className="lg:col-span-6">
            <Reveal>
              <p className="overline-label">Built to grow with you</p>
              <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                Founded on experience. Driven by learning.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                Evol &amp; Troppus is led by a certified Behavior Technician (BT/RBT) with
                years of hands-on experience working directly with children and families in
                homes and schools. That front-line perspective shapes everything here:
                practical over theoretical, honest about scope, and committed to lifelong
                learning and evidence-informed practice. Today, we focus on parent coaching,
                family coaching, and caregiver education — never diagnosis, assessment, or
                clinical treatment. Tomorrow, we are growing into professional trainings,
                digital courses, memberships, and new tools. What will never change: people
                before protocols, evidence before trends, and caregiver confidence as the
                measure of success.
              </p>
              <Link to="/services" data-testid="about-services-link" className="btn-primary mt-8">
                Explore our services <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
